import React, { useState, useRef, useEffect } from 'react';
import { Input, Button, Select, DatePicker, Space, Tag, Dropdown, Menu } from 'antd';
import { SendOutlined, ClearOutlined, PlusOutlined, FilterOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { ChatModule } from '../../api/aiChatApi';
import { Dayjs } from 'dayjs';

const { TextArea } = Input;
const { RangePicker } = DatePicker;

interface ChatInputProps {
  onSendMessage: (prompt: string, module: ChatModule, dateRange?: string[], categories?: string[]) => void;
  loading?: boolean;
  showQuickPrompts?: boolean;
  showFilters?: boolean;
  onToggleQuickPrompts?: () => void;
  onToggleFilters?: () => void;
}

const ChatInput: React.FC<ChatInputProps> = ({ 
  onSendMessage, 
  loading, 
  showQuickPrompts = false, 
  showFilters = false,
  onToggleQuickPrompts,
  onToggleFilters 
}) => {
  const [prompt, setPrompt] = useState('');
  const [module, setModule] = useState<ChatModule>(ChatModule.EXPENSES);
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [categories, setCategories] = useState<string[]>([]);
  const [dropdownVisible, setDropdownVisible] = useState(false);

  const handleSend = () => {
    if (!prompt.trim()) return;

    const dateRangeStr = dateRange
      ? [dateRange[0].format('YYYY-MM-DD'), dateRange[1].format('YYYY-MM-DD')]
      : undefined;

    onSendMessage(prompt, module, dateRangeStr, categories.length > 0 ? categories : undefined);
    
    // Clear prompt after sending
    setPrompt('');
    
    // Close all + options after sending
    if (showQuickPrompts) {
      onToggleQuickPrompts?.();
    }
    if (showFilters) {
      onToggleFilters?.();
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleClear = () => {
    setPrompt('');
    setDateRange(null);
    setCategories([]);
  };

  // Handle option toggle with mutual exclusivity
  const handleToggleQuickPrompts = () => {
    onToggleQuickPrompts?.();
    // Close filters if quick prompts is being opened
    if (!showQuickPrompts && showFilters) {
      onToggleFilters?.();
    }
    setDropdownVisible(false);
  };

  const handleToggleFilters = () => {
    onToggleFilters?.();
    // Close quick prompts if filters is being opened
    if (!showFilters && showQuickPrompts) {
      onToggleQuickPrompts?.();
    }
    setDropdownVisible(false);
  };

  // Handle input click to close all options
  const handleInputClick = () => {
    if (showQuickPrompts) {
      onToggleQuickPrompts?.();
    }
    if (showFilters) {
      onToggleFilters?.();
    }
  };

  // Dropdown menu items
  const dropdownMenu = (
    <Menu className="w-48">
      <Menu.Item 
        key="quick-prompts" 
        icon={<ThunderboltOutlined />}
        onClick={handleToggleQuickPrompts}
      >
        Quick Prompts
      </Menu.Item>
      <Menu.Item 
        key="filters" 
        icon={<FilterOutlined />}
        onClick={handleToggleFilters}
      >
        Filters
      </Menu.Item>
    </Menu>
  );

  const quickPrompts = [
    'How many users are registered in ParkingLap?',
    'Show me expense trends this month',
    'What are the most active user roles?',
    'Analyze my business expenses by category',
  ];

  const categoryOptions = [
    { label: 'Nakipur Land', value: 'Nakipur Land' },
    { label: 'Project', value: 'Project' },
    { label: 'Software', value: 'Software' },
    { label: 'Business', value: 'Business' },
    { label: 'Personal', value: 'Personal' },
  ];

  return (
    <div className="bg-white p-4">
      {/* Quick Prompts - Conditional */}
      {showQuickPrompts && (
        <div className="mb-3">
          <div className="text-xs text-gray-500 mb-2">Quick prompts:</div>
          <div className="flex flex-wrap gap-2">
            {quickPrompts.map((qp, index) => (
              <Tag
                key={index}
                className="cursor-pointer hover:bg-blue-50"
                onClick={() => setPrompt(qp)}
              >
                {qp}
              </Tag>
            ))}
          </div>
        </div>
      )}

      {/* Filters - Conditional */}
      {showFilters && (
        <div className="mb-3">
          <Space wrap className="w-full">
            <Select
              value={module}
              onChange={setModule}
              style={{ width: 150 }}
              options={[
                { label: 'Expenses', value: ChatModule.EXPENSES },
                { label: 'Users', value: ChatModule.USERS },
                { label: 'General', value: ChatModule.GENERAL },
              ]}
            />
            
            <RangePicker
              value={dateRange}
              onChange={(dates) => setDateRange(dates as [Dayjs, Dayjs] | null)}
              placeholder={['Start Date', 'End Date']}
              style={{ width: 250 }}
            />

            <Select
              mode="multiple"
              placeholder="Select categories"
              value={categories}
              onChange={setCategories}
              style={{ minWidth: 200, maxWidth: 400 }}
              options={categoryOptions}
              maxTagCount="responsive"
            />

            <Button
              icon={<ClearOutlined />}
              onClick={handleClear}
              size="small"
            >
              Clear Filters
            </Button>
          </Space>
        </div>
      )}

      {/* Input Area - Borderless Full Height */}
      <div className="flex items-center bg-white px-6 py-4 min-h-[80px]">
        {/* + Icon */}
        <div className="flex items-center mr-2">
          <Dropdown
            overlay={dropdownMenu}
            trigger={['click']}
            visible={dropdownVisible}
            onVisibleChange={setDropdownVisible}
            placement="topLeft"
          >
            <Button
              type="text"
              icon={<PlusOutlined />}
              className="flex-shrink-0 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full"
              size="small"
              title="Add options"
            />
          </Dropdown>
        </div>
        
        {/* Text Input - Expanded */}
        <div className="flex-1">
          <TextArea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyPress={handleKeyPress}
            onClick={handleInputClick}
            placeholder="Ask me about ParkingLap users, expenses, or data insights..."
            autoSize={{ minRows: 1, maxRows: 6 }}
            disabled={loading}
            className="border-0 shadow-none resize-none focus:shadow-none bg-transparent"
            style={{ 
              fontSize: '16px',
              lineHeight: '24px',
              padding: '12px 16px',
              background: 'transparent',
              minHeight: '48px'
            }}
          />
        </div>
        
        {/* Send Button */}
        <div className="flex items-center ml-2">
          <Button
            type="primary"
            shape="circle"
            icon={<SendOutlined />}
            onClick={handleSend}
            loading={loading}
            disabled={!prompt.trim() || loading}
            size="small"
            className="bg-blue-600 hover:bg-blue-700 border-0 shadow-none"
            style={{ 
              width: '32px', 
              height: '32px',
              minWidth: '32px'
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default ChatInput;

